import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// --- Auth strategy: httpOnly cookies only ---
// The JWT itself lives ONLY in an httpOnly, Secure, SameSite=Lax cookie set
// by the backend on login (see auth.py: set_auth_cookie). It is never
// exposed to client-side JS, so it can't be read/exfiltrated via XSS.
// `withCredentials: true` below ensures the browser attaches that cookie to
// every API request automatically - no Authorization header needed.
//
// No auth/profile data is kept in localStorage. "Is there a session?" is read
// from the non-secret companion marker cookie and the cached display profile
// lives in tab-scoped sessionStorage - see lib/session.js.
import { hasAdminSession, clearAdminSession, migrateLegacyStorage } from "@/lib/session";
migrateLegacyStorage();
export { clearAdminSession };

// Every backend router prefix that requires the CRM/admin (staff) token rather
// than the storefront shop token. Kept as a single allowlist so adding a new
// admin/ERP module (a new backend router mounted under its own /api/<prefix>)
// only requires appending one entry here instead of growing an inline string
// of startsWith() checks — new modules are covered automatically as long as
// they're added to this list.
const ADMIN_ROUTE_PREFIXES = ["admin", "crm", "purchase", "karigar", "inventory", "opening"];

// 30s cap so a hung/unresponsive backend surfaces a normal error (handled by page
// catch blocks) instead of an indefinite "Loading…" — the reported refresh-stuck bug.
export const api = axios.create({ baseURL: API, withCredentials: true, timeout: 30000 });

// Auth-failure details that mean "this token/identity is dead" (vs a legitimate
// permission denial like "Only Admin/Managers can …", which must NOT log anyone out).
const AUTH_FAILURE_DETAILS = [
  "not authenticated",
  "admin access required",
  "staff access required",
  "session expired",
  "invalid token",
];
let redirectingToLogin = false;

api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    if (status === 401 || status === 403) {
      const url = String(error?.config?.url || "").replace(/^\//, "");
      const isAdminRoute = ADMIN_ROUTE_PREFIXES.some((prefix) => url.startsWith(prefix));
      const detail = String(error?.response?.data?.detail || "").toLowerCase();
      const identityFailure = status === 401 || AUTH_FAILURE_DETAILS.some((d) => detail.includes(d));
      // Only the ADMIN session is auto-cleared: storefront guests legitimately get 401 on
      // the login-gated catalog, and redirecting them would create a login loop.
      // A dead/expired admin cookie must never wedge the UI: clear the marker + cached
      // profile so admin pages don't hang on "Loading..." until a manual logout.
      if (isAdminRoute && identityFailure && hasAdminSession()) {
        clearAdminSession();
        if (!redirectingToLogin && !window.location.pathname.startsWith("/admin/login")) {
          redirectingToLogin = true;
          window.location.assign("/admin/login");
        }
      }
    }
    return Promise.reject(error);
  }
);

export function formatINR(value) {
  if (value === null || value === undefined || isNaN(value)) return "₹0";
  return "₹" + Math.round(value).toLocaleString("en-IN");
}

// Resolve an image src: uploaded files return a relative /api/files/... path,
// while seed data uses absolute http(s) URLs. Prefix backend URL for relative paths.
export function resolveImg(src) {
  if (!src) return "";
  if (src.startsWith("http://") || src.startsWith("https://") || src.startsWith("data:")) return src;
  if (src.startsWith("/api/")) return `${BACKEND_URL}${src}`;
  return src;
}

// Download an authenticated file (PDF/CSV) via axios blob and trigger browser save.
export async function downloadBlob(path, filename) {
  const resp = await api.get(path, { responseType: "blob" });
  const url = window.URL.createObjectURL(new Blob([resp.data]));
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

// Same as downloadBlob but for POST endpoints (e.g. bulk export of selected rows).
export async function postDownloadBlob(path, body, filename) {
  const resp = await api.post(path, body, { responseType: "blob" });
  const url = window.URL.createObjectURL(new Blob([resp.data]));
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

export function formatNum(value, digits = 2) {
  if (value === null || value === undefined || isNaN(value)) return "0";
  return Number(value).toLocaleString("en-IN", { maximumFractionDigits: digits });
}
